import { GeoCoordinates, LocationBoundary, MILES_TO_METERS } from '@aloft/toolkit-shared';

import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { useZodFormContext } from '../../../hooks/useZodForm.ts';
import { RecommendCompsFormSchema } from '../types.ts';

export const useGeoSearchFilterControls = ({
  subjectLat,
  subjectLng,
  locationBoundary,
}: {
  subjectLat: number;
  subjectLng: number;
  locationBoundary?: LocationBoundary;
}) => {
  const { control, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const geoSearch = useWatch({ name: 'search.geoSearch', control });
  const [radius, setRadius] = useState<'' | number | undefined>();

  useEffect(() => {
    if (geoSearch.t === 'radius' && radius === undefined) {
      setRadius(geoSearch.radiusInMeters / MILES_TO_METERS);
    }
  }, [geoSearch.t]);

  useEffect(() => {
    if (geoSearch.t !== 'municipality' || !locationBoundary) return;
    if (geoSearch.name === locationBoundary.name && geoSearch.type === locationBoundary.type) {
      return;
    }
    setValue('search.geoSearch', {
      t: 'municipality',
      type: locationBoundary.type,
      name: locationBoundary.name,
    });
  }, [geoSearch, locationBoundary, setValue]);

  const handleRadiusChange = (value: '' | number | undefined) => {
    if (typeof value !== 'number') {
      setRadius(value);
      return;
    }

    if (value < 0) {
      setRadius(0);
    } else if (value > 100) {
      setRadius(100);
    } else {
      setRadius(value);
      if (geoSearch.t === 'radius') {
        setValue('search.geoSearch.radiusInMeters', value * MILES_TO_METERS);
      }
    }
  };

  const handleGeoSearchTypeChange = (value: string) => {
    if (value === 'radius') {
      setValue('search.geoSearch', {
        t: 'radius',
        radiusInMeters: radius ? radius * MILES_TO_METERS : MILES_TO_METERS,
        coord: [subjectLng, subjectLat],
      });
      return;
    }

    if (value === 'municipality' && locationBoundary) {
      setValue('search.geoSearch', {
        t: 'municipality',
        type: locationBoundary.type,
        name: locationBoundary.name,
      });
      return;
    }

    setValue('search.geoSearch', { t: 'polygon', coords: [] });
  };

  const onPolygonCreateAndUpdate = (coordinates: GeoCoordinates) => {
    setValue('search.geoSearch', { t: 'polygon', coords: coordinates });
  };

  const onPolygonDrawDelete = () => {
    setValue('search.geoSearch', { t: 'polygon', coords: [] });
  };

  return {
    geoSearch,
    radius,
    setRadius,
    handleRadiusChange,
    handleGeoSearchTypeChange,
    onPolygonCreateAndUpdate,
    onPolygonDrawDelete,
  };
};
